import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgentTemplate, CatalogTemplate, Workspace } from '../types';
import { listTemplates, deleteTemplate, listWorkspaces, listCatalogTemplates } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

export function TemplateSetup() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState<(AgentTemplate | CatalogTemplate)[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      if (isAuthenticated) {
        const [tpls, wss] = await Promise.all([listTemplates(), listWorkspaces()]);
        setTemplates(tpls);
        setWorkspaces(wss);
      } else {
        setTemplates(await listCatalogTemplates());
        setWorkspaces([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const getWorkspacesForTemplate = (templateId: string) =>
    workspaces.filter((w) => w.templateIds.includes(templateId));

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={styles.heading}>Templates</h2>
        {isAuthenticated && (
          <button style={styles.createBtn} onClick={() => navigate('/templates/new')}>+ New Template</button>
        )}
      </div>
      {loading ? (
        <div style={styles.muted}>Loading...</div>
      ) : templates.length === 0 ? (
        <div style={styles.muted}>No templates yet</div>
      ) : (
        <div style={styles.list}>
          {templates.map((t) => {
            const tplWorkspaces = getWorkspacesForTemplate(t.id);
            return (
              <div key={t.id} style={styles.card} onClick={() => isAuthenticated ? navigate(`/templates/${t.id}`) : undefined}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{t.name}</strong>
                  <span style={styles.meta}>{t.language} {'isPublic' in t && !t.isPublic ? '(private)' : ''}</span>
                </div>
                {t.description && <div style={styles.desc}>{t.description}</div>}
                {'sttProvider' in t && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span>STT: <strong>{t.sttProvider}</strong></span>
                    <span>TTS: <strong>{t.ttsProvider}</strong></span>
                    <span>LLM: <strong>{t.llmModel}</strong></span>
                    <span style={{ color: '#aaa' }}>| {t.customFields.length} custom fields</span>
                  </div>
                )}
                {tplWorkspaces.length > 0 && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    Workspaces: {tplWorkspaces.map((w) => w.name).join(', ')}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button style={styles.agentBtn} onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/templates/${t.id}/create-agent`);
                  }}>Create Agent</button>
                  {isAuthenticated && (
                    <button style={styles.deleteBtn} onClick={(e) => {
                      e.stopPropagation();
                      deleteTemplate(t.id).then(load);
                    }}>Delete</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '20px', flex: 1, overflowY: 'auto' },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  createBtn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  agentBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #4f46e5', background: '#fff', color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  deleteBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer' },
  muted: { color: '#888', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: { padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', cursor: 'pointer' },
  meta: { fontSize: 12, color: '#888' },
  desc: { fontSize: 13, color: '#666', marginTop: 4 },
};
